import type React from "react";

/**
 * The app's toasts, owned here rather than by a library (#2967).
 *
 * Every feature raises a toast through this module and nothing else, so the library behind it can
 * change in one file. Until #2967 that library was notistack, which stopped shipping releases in
 * January 2025; this queue now does what the app used from it, and `AppSnackbarProvider` draws it with
 * MUI's `Snackbar` and `Alert`.
 *
 * The queue lives at module level so code outside React — a Redux slice, an RTK Query callback — can
 * raise a toast with `enqueueSnackbar` without a hook. The provider only subscribes to it.
 */

export type SnackbarVariant = "success" | "error" | "warning" | "info";

export type SnackbarKey = string | number;

export type SnackbarOptions = {
  readonly variant: SnackbarVariant;
  /** Names the toast, so `closeSnackbar` can close it and `preventDuplicate` can compare it. */
  readonly key?: SnackbarKey;
  /** Stays until the person closes it, instead of hiding after `autoHideDuration`. */
  readonly persist?: boolean;
  /**
   * Shows nothing when a toast with the same key — or, without a key, the same message — is already
   * shown or waiting.
   */
  readonly preventDuplicate?: boolean;
  /** Milliseconds before the toast hides itself. Defaults to five seconds. */
  readonly autoHideDuration?: number;
  /** Drawn after the message, before the close button: an Undo, say. Given the toast's key. */
  readonly action?: (key: SnackbarKey) => React.ReactNode;
  /** Spread onto the toast's root element, for an id or hover and focus handlers. */
  readonly snackbarProps?: React.HTMLAttributes<HTMLDivElement>;
  /**
   * How a screen reader hears the toast. `alert` interrupts, which suits a failure; `status` waits its
   * turn, which suits a confirmation that offers Undo. Defaults to `alert`.
   */
  readonly role?: "alert" | "status";
  /** Called once, when the toast is asked to close: by its timer, its close button, or for room. */
  readonly onClose?: () => void;
};

export type Snackbar = {
  readonly key: SnackbarKey;
  readonly message: React.ReactNode;
  readonly variant: SnackbarVariant;
  /** `null` for a toast that persists. */
  readonly autoHideDuration: number | null;
  /** False from the moment the toast is asked to close until its exit transition ends. */
  readonly open: boolean;
  readonly action?: (key: SnackbarKey) => React.ReactNode;
  readonly snackbarProps?: React.HTMLAttributes<HTMLDivElement>;
  readonly role?: "alert" | "status";
  readonly onClose?: () => void;
};

export const MAX_VISIBLE_SNACKBARS = 3;

export const DEFAULT_AUTO_HIDE_DURATION_MS = 5_000;

/**
 * Up to three toasts on screen, the rest waiting in order.
 *
 * A toast that arrives while three are shown waits, and the oldest one that does not persist is asked
 * to close to make room for it. When all three persist, the oldest of those closes instead, because a
 * page that raised three persisting toasts must still be able to say something new. Only one toast is
 * closed for room at a time: while one is leaving, the next arrival waits for it.
 */
export class SnackbarQueue {
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** The toasts on screen, oldest first. A new array whenever anything changes. */
  public readonly getVisible = (): readonly Snackbar[] => this.visible;

  public readonly enqueue = (message: React.ReactNode, options: SnackbarOptions): SnackbarKey => {
    const hasKey = options.key !== undefined;
    if (options.preventDuplicate) {
      const duplicate = [...this.visible, ...this.waiting].find((snackbar) =>
        hasKey ? snackbar.key === options.key : snackbar.message === message,
      );
      if (duplicate) return duplicate.key;
    }

    this.nextKey += 1;
    const snackbar: Snackbar = {
      key: hasKey ? (options.key as SnackbarKey) : `snackbar-${this.nextKey}`,
      message,
      variant: options.variant,
      autoHideDuration: options.persist ? null : (options.autoHideDuration ?? DEFAULT_AUTO_HIDE_DURATION_MS),
      open: true,
      action: options.action,
      snackbarProps: options.snackbarProps,
      role: options.role,
      onClose: options.onClose,
    };
    this.waiting = [...this.waiting, snackbar];
    const evicted = this.admitWaiting();
    this.notify();
    evicted?.onClose?.();
    return snackbar.key;
  };

  /** Closes one toast, or every toast — shown and waiting — when no key is given. */
  public readonly close = (key?: SnackbarKey): void => {
    const closes = (snackbar: Snackbar) => key === undefined || snackbar.key === key;
    const closing = [...this.visible.filter((snackbar) => snackbar.open), ...this.waiting].filter(closes);
    this.waiting = this.waiting.filter((snackbar) => !closes(snackbar));
    this.visible = this.visible.map((snackbar) =>
      closes(snackbar) && snackbar.open ? { ...snackbar, open: false } : snackbar,
    );
    this.notify();
    for (const snackbar of closing) snackbar.onClose?.();
  };

  /** Called once a closed toast has finished leaving the screen; makes room for the next one. */
  public readonly remove = (key: SnackbarKey): void => {
    this.visible = this.visible.filter((snackbar) => snackbar.key !== key);
    const evicted = this.admitWaiting();
    this.notify();
    evicted?.onClose?.();
  };

  private visible: readonly Snackbar[] = [];
  private waiting: readonly Snackbar[] = [];
  private nextKey = 0;
  private readonly listeners = new Set<() => void>();

  /** Returns the toast asked to close to make room, if one was. */
  private admitWaiting(): Snackbar | undefined {
    let visible = this.visible;
    let waiting = this.waiting;
    while (waiting.length > 0 && visible.length < MAX_VISIBLE_SNACKBARS) {
      visible = [...visible, waiting[0]];
      waiting = waiting.slice(1);
    }

    let evicted: Snackbar | undefined;
    if (waiting.length > 0 && visible.every((snackbar) => snackbar.open)) {
      evicted = visible.find((snackbar) => snackbar.autoHideDuration !== null) ?? visible[0];
      visible = visible.map((snackbar) => (snackbar === evicted ? { ...snackbar, open: false } : snackbar));
    }

    this.visible = visible;
    this.waiting = waiting;
    return evicted;
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}

export const snackbarQueue = new SnackbarQueue();

/** Raises a toast from anywhere, inside React or out. Returns its key. */
export function enqueueSnackbar(message: React.ReactNode, options: SnackbarOptions): SnackbarKey {
  return snackbarQueue.enqueue(message, options);
}

/** Closes the toast with this key, or every toast when no key is given. */
export function closeSnackbar(key?: SnackbarKey): void {
  snackbarQueue.close(key);
}

const SNACKBAR_ACTIONS = { enqueueSnackbar, closeSnackbar } as const;

/**
 * The same two functions, for components that prefer a hook. The object never changes, so it is safe
 * in a dependency array.
 */
export function useSnackbar(): typeof SNACKBAR_ACTIONS {
  return SNACKBAR_ACTIONS;
}
